import { FC, useState } from 'react';
import { Button, Label } from 'reactstrap';

import { ActionTile } from '@g17eco/atoms/action-tile/ActionTile';
import { Avatar } from '@g17eco/atoms/avatar/Avatar';
import { CollaborationAvatar } from '@g17eco/atoms/avatar/CollaborationAvatar';
import { BasicContainer } from '@g17eco/atoms/basic-container/BasicContainer';
import { ButtonGlow } from '@g17eco/atoms/button/ButtonGlow';
import { ButtonGradient } from '@g17eco/atoms/button/ButtonGradient';
import { Card, CardBody } from '@g17eco/atoms/card/Card';
import { Checkbox, CheckboxState } from '@g17eco/atoms/checkbox/Checkbox';
import { CounterWidget } from '@g17eco/atoms/counter-widget/CounterWidget';
import { DashboardDivider } from '@g17eco/atoms/divider/DashboardDivider';
import { VerticalDivider } from '@g17eco/atoms/divider/VerticalDivider';
import { BlockingLoader } from '@g17eco/atoms/loader/BlockingLoader';
import { Loader } from '@g17eco/atoms/loader/Loader';
import { ProgressLoader } from '@g17eco/atoms/loader/ProgressLoader';
import { SectionLoader } from '@g17eco/atoms/loader/SectionLoader';
import LoadingAnimation from '@g17eco/atoms/loading-animation';
import LoadingButton from '@g17eco/atoms/loading-button';
import NotFound from '@g17eco/atoms/not-found';
import { ShowcaseItem } from '@routes/theme-showcase/components/ShowcaseSection';
import { useAppSelector } from '@reducers/index';
import { getCurrentUser } from '@selectors/user';

export const AtomsShowcase: FC = () => {
  const [checked, setChecked] = useState(CheckboxState.Unchecked);
  const [blockingLoaderVisible, setBlockingLoaderVisible] = useState(false);
  const [loadingBtnState, setLoadingBtnState] = useState({ btn1: false, btn2: true });

  const user = useAppSelector(getCurrentUser);

  const toggleBlockingLoader = () => {
    setBlockingLoaderVisible(true);
    setTimeout(() => setBlockingLoaderVisible(false), 3000);
  };

  return (
    <>
      <ShowcaseItem title='ActionTile'>
        <div className='d-flex gap-3 flex-wrap'>
          <div style={{ width: '200px' }}>
            <ActionTile icon='fa-plus' title='Sample Tile' onClick={() => {}} />
          </div>
          <div style={{ width: '200px' }}>
            <ActionTile icon='fa-image' title='Media' subtitle='With subtitle text' onClick={() => {}} />
          </div>
        </div>
      </ShowcaseItem>

      <ShowcaseItem title='Avatar'>
        <div className='d-flex gap-3 align-items-center flex-column'>
          <Avatar width='40px'>{user?.profile ? <img alt='avatar' src={user.profile as string} /> : undefined}</Avatar>
          <p>CollaborationAvatar With Profile:</p>
          <CollaborationAvatar name={user?.name || 'User Name'} size={40} profile={user?.profile || undefined} />
          <p>CollaborationAvatar Without Profile:</p>
          <CollaborationAvatar name={user?.name || 'User Name'} size={40} />
        </div>
      </ShowcaseItem>

      <ShowcaseItem title='BasicContainer'>
        <BasicContainer>
          <div className='p-3'>
            <strong>Content inside Basic Container</strong>
            <p className='mb-0 text-ThemeTextLight'>This container applies standard padding and background styles.</p>
          </div>
        </BasicContainer>
      </ShowcaseItem>

      <ShowcaseItem title='Buttons (Atoms)'>
        <div className='d-flex gap-2'>
          <ButtonGradient>Gradient Button</ButtonGradient>
          <ButtonGlow color='primary'>Glow Button</ButtonGlow>
        </div>
      </ShowcaseItem>

      <ShowcaseItem title='Card'>
        <div className='d-flex gap-3 flex-wrap'>
          <div style={{ maxWidth: '300px' }}>
            <Card>
              <CardBody>
                <h5 className='card-title'>Default Card</h5>
                <p className='card-text'>Standard shadow (sm)</p>
              </CardBody>
            </Card>
          </div>
          <div style={{ maxWidth: '300px' }}>
            <Card shadow='none'>
              <CardBody>
                <h5 className='card-title'>No Shadow</h5>
                <p className='card-text'>shadow='none'</p>
              </CardBody>
            </Card>
          </div>
          <div style={{ maxWidth: '300px' }}>
            <Card shadow='lg'>
              <CardBody>
                <h5 className='card-title'>Large Shadow</h5>
                <p className='card-text'>shadow='lg'</p>
              </CardBody>
            </Card>
          </div>
          <div style={{ maxWidth: '300px' }}>
            <Card hover>
              <CardBody>
                <h5 className='card-title'>Hover Effect</h5>
                <p className='card-text'>Hover to see background change</p>
              </CardBody>
            </Card>
          </div>
        </div>
      </ShowcaseItem>

      <ShowcaseItem title='Checkbox'>
        <div className='d-flex flex-column gap-2'>
          <div className='d-flex align-items-center'>
            <Checkbox status={CheckboxState.Unchecked} onChange={() => {}} />
            <Label className='ml-2 mb-0'>Unchecked (Static)</Label>
          </div>
          <div className='d-flex align-items-center'>
            <Checkbox status={CheckboxState.Checked} onChange={() => {}} />
            <Label className='ml-2 mb-0'>Checked (Static)</Label>
          </div>
          <div className='d-flex align-items-center'>
            <Checkbox status={CheckboxState.Indeterminate} onChange={() => {}} />
            <Label className='ml-2 mb-0'>Indeterminate (Static)</Label>
          </div>
          <div className='d-flex align-items-center'>
            <Checkbox
              status={checked}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setChecked(e.target.checked ? CheckboxState.Checked : CheckboxState.Unchecked)
              }
            />
            <Label className='ml-2 mb-0'>
              <strong>Interactive:</strong> {checked === CheckboxState.Checked ? 'Checked' : 'Unchecked'}
            </Label>
          </div>
        </div>
      </ShowcaseItem>

      <ShowcaseItem title='CounterWidget'>
        <div className='d-flex gap-4 align-items-center'>
          <div className='text-center'>
            <div className='mb-1 small text-ThemeTextLight'>Default (md)</div>
            <CounterWidget count={5} />
          </div>
          <div className='text-center'>
            <div className='mb-1 small text-ThemeTextLight'>Large</div>
            <CounterWidget count={100} size='lg' />
          </div>
          <div className='text-center'>
            <div className='mb-1 small text-ThemeTextLight'>Small</div>
            <CounterWidget count={0} size='sm' />
          </div>
          <div className='text-center'>
            <div className='mb-1 small text-ThemeTextLight'>Dark Appearance</div>
            <CounterWidget count={3} appearance='dark' />
          </div>
        </div>
      </ShowcaseItem>

      <ShowcaseItem title='Dividers'>
        <div className='w-100 p-3 border rounded'>
          <p>Section 1</p>
          <DashboardDivider />
          <p>Section 2 (DashboardDivider above)</p>
          <hr />
          <div className='d-flex align-items-center' style={{ height: '40px' }}>
            <span className='me-2'>Left Side</span>
            <VerticalDivider />
            <span className='ms-2'>Right Side (VerticalDivider)</span>
          </div>
        </div>
      </ShowcaseItem>

      <ShowcaseItem title='Loaders (Relative)'>
        <div className='d-flex gap-4 align-items-center w-100 flex-wrap'>
          <div className='position-relative p-4 border' style={{ minWidth: '150px', minHeight: '150px' }}>
            <Loader />
            <div className='position-absolute bottom-0 w-100 text-center pb-2 small text-ThemeTextLight'>Loader</div>
          </div>
          <div className='position-relative p-4 border' style={{ minWidth: '200px', minHeight: '150px' }}>
            <ProgressLoader />
            <div className='position-absolute bottom-0 w-100 text-center pb-2 small text-ThemeTextLight'>
              ProgressLoader
            </div>
          </div>
          <div className='position-relative p-4 border w-100' style={{ minHeight: '150px' }}>
            <SectionLoader />
            <div className='position-absolute bottom-0 w-100 text-center pb-2 small text-ThemeTextLight'>
              SectionLoader
            </div>
          </div>
        </div>
      </ShowcaseItem>

      <ShowcaseItem title='BlockingLoader'>
        <div className='d-flex flex-column gap-2'>
          <p>Click the button below to simulate a blocking network request (3 seconds).</p>
          <Button onClick={toggleBlockingLoader}>Trigger Blocking Loader</Button>
          {blockingLoaderVisible && <BlockingLoader />}
        </div>
      </ShowcaseItem>

      <ShowcaseItem title='LoadingAnimation'>
        <div style={{ width: '100px', height: '35px' }}>
          <LoadingAnimation />
        </div>
      </ShowcaseItem>

      <ShowcaseItem title='LoadingButton'>
        <div className='d-flex gap-2 flex-wrap'>
          <LoadingButton
            loading={loadingBtnState.btn1}
            loadingText='Saving...'
            onClick={() => {
              setLoadingBtnState((prev) => ({ ...prev, btn1: true }));
              setTimeout(() => setLoadingBtnState((prev) => ({ ...prev, btn1: false })), 2000);
            }}
          >
            Click to Load
          </LoadingButton>

          <LoadingButton
            loading={loadingBtnState.btn2}
            loadingText='Processing...'
            onClick={() => setLoadingBtnState((prev) => ({ ...prev, btn2: !prev.btn2 }))}
          >
            Toggle State
          </LoadingButton>
        </div>
      </ShowcaseItem>

      <ShowcaseItem title='NotFound'>
        <div className='border p-0 w-100 overflow-hidden' style={{ height: '300px', position: 'relative' }}>
          <NotFound />
        </div>
      </ShowcaseItem>
    </>
  );
};
